import { Component, Input, OnDestroy, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Subject, noop } from 'rxjs';
import { Store } from '@ngrx/store';
import { takeUntil, map, take, filter } from 'rxjs/operators';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import {
  InquiryDateResponse,
  BaseInquiryDate,
  BaseCoreState,
  baseInquirySelectors,
  ShiftPlanCalendarDateInterface,
  BASE_INQUIRY_CALENDAR_DAY_COLUMN_WIDTH,
  BASE_INQUIRY_CALENDAR_BUFFER_PX_SIZE,
  BaseModalService,
  ShiftTimeResponse,
  ShiftPlanListBubbleItemInterface,
  BaseInquiryScrollService,
  ShiftPlanCalendarRangeInterface,
  BaseInquiryShiftsWithFlexType,
} from 'base';

@Component({
  selector: 'base-customer-shift-plan-dates-list',
  templateUrl: './base-customer-shift-plan-calendar-dates.component.html',
})
export class BaseCustomerShiftPlanCalendarDatesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('shiftsUpdateModal') shiftsUpdateModal: ElementRef;
  @Input() timeShifts: ShiftTimeResponse[] = [];

  modalRef: NgbModalRef;
  dayColumnWidth = BASE_INQUIRY_CALENDAR_DAY_COLUMN_WIDTH;
  baseInquiryBufferPx = BASE_INQUIRY_CALENDAR_BUFFER_PX_SIZE;
  inquiryDates: InquiryDateResponse[] = [];
  datesArray: BaseInquiryDate[] = [];
  bubbleList: ShiftPlanCalendarDateInterface[] = [];
  data: ShiftPlanCalendarDateInterface;
  currentShift: BaseInquiryShiftsWithFlexType;
  shiftTimes: Array<ShiftTimeResponse> = [];
  ranges: ShiftPlanCalendarRangeInterface[] = [];
  visibleDatesAmount: number;
  calendarScrollPosition: number;

  private destroy$ = new Subject();

  constructor(
    private store: Store<BaseCoreState>,
    private modalService: BaseModalService,
    private baseInquiryScrollService: BaseInquiryScrollService
  ) {}

  ngOnInit() {
    this.store
      .select(baseInquirySelectors.getShiftPlanList)
      .pipe(
        takeUntil(this.destroy$),
        map((data) => data.list as ShiftPlanCalendarDateInterface[])
      )
      .subscribe((data) => {
        this.bubbleList = data;
      });

    this.store
      .select(baseInquirySelectors.getShiftPlanRangeList)
      .pipe(
        takeUntil(this.destroy$),
        filter((data) => !!data)
      )
      .subscribe((ranges) => {
        this.ranges = ranges;
      });

    this.baseInquiryScrollService.scrollOffset$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.calendarScrollPosition = value;
    });
  }

  ngAfterViewInit() {
    this.baseInquiryScrollService.visibleDatesQty$.pipe(take(1)).subscribe((value) => {
      this.visibleDatesAmount = value;
    });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  bubbleClick(shiftType: BaseInquiryShiftsWithFlexType, data: ShiftPlanCalendarDateInterface) {
    this.data = data;
    this.currentShift = shiftType;

    this.openEditShiftModal();
  }

  openEditShiftModal() {
    this.modalRef = this.modalService.open(
      {
        headText: 'shiftModalTitle',
        bodyTemplate: this.shiftsUpdateModal,
        hideFooter: true,
      },
      {
        backdrop: 'static',
        size: 'lg',
      }
    );

    this.modalRef.result.then((date: string | null) => {
      this.handleDateSelection(date);
    }, noop);
  }

  getFirstDateIndexInRange(date: string): number {
    return Math.max(
      0,
      this.bubbleList.findIndex((item) => item.dateItem.dateString === date)
    );
  }

  trackByDay(_: number, item: ShiftPlanCalendarDateInterface) {
    return item.dateItem.dateString;
  }

  trackByBubble(_: number, item: ShiftPlanListBubbleItemInterface) {
    return item.shift;
  }

  private handleDateSelection(date: string | null): void {
    if (!date) {
      return;
    }

    const startOffset = this.getFirstDateIndexInRange(date);

    if (this.ranges.length === 0) {
      this.baseInquiryScrollService.setSliderOffsetValue(0);
      return;
    }

    const added = this.ranges.find((r) => r.rangeData.startDate === date);
    if (!added) {
      return;
    }

    const duration = added.dates.length;
    const endOffset = this.getFirstDateIndexInRange(added.rangeData.endDate!);
    const visibleAfterEnd = this.visibleDatesAmount + this.calendarScrollPosition - endOffset;
    const shouldScroll =
      visibleAfterEnd < 5 || duration > this.visibleDatesAmount || startOffset < this.calendarScrollPosition;

    if (!shouldScroll) {
      return;
    }

    setTimeout(() => {
      const newOffset =
        duration >= this.visibleDatesAmount
          ? endOffset - 6 // if the range is longer than the viewport, show the last week
          : startOffset;
      this.baseInquiryScrollService.setSliderOffsetValue(newOffset);
    });
  }
}
